import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { XIcon } from './Icons'
import { OHNE, grundStil, type Ausschnitt } from '../lib/ausschnitt'
import { useTexte } from '../texte'

/**
 * Welchen Ausschnitt eines Bildes man sieht — ziehen und größer stellen (#51).
 *
 * **Ein Blatt für beide Bilder**, das Symbolbild und den Hintergrund des
 * Chats. Sie unterscheiden sich in genau einem Wert: wie klein das Bild werden
 * darf. Der Hintergrund darf unter „füllt die Fläche" — dann steht das ganze
 * Bild auf der gewählten Farbe. Das Symbolbild darf es nicht: eine Lücke im
 * runden Feld der Übersicht wäre schlicht kaputt.
 *
 * **Der Rahmen hat die Form der Zielfläche**, und zwar der auf diesem Gerät.
 * Ein fester Rahmen zeigte sonst einen Ausschnitt, den es hier gar nicht gibt
 * — der Chat ist am iPhone hochkant und am iPad fast quadratisch.
 *
 * **Gerechnet wird mit `grundStil`**, derselben Funktion, die den Chat
 * hinterlegt. Eine eigene Vorschaurechnung wäre dieselbe Regel ein zweites Mal
 * und zeigte über kurz oder lang etwas anderes als die Anwendung.
 */
export function Ausschnittwahl({
  bildUrl,
  ausschnitt,
  form,
  kleinste,
  titel,
  onFertig,
  onSchliessen,
}: {
  bildUrl: string
  ausschnitt: Ausschnitt
  /** Die Form der Zielfläche — Breite zu Höhe. */
  form: { breite: number; hoehe: number }
  /** Die kleinste erlaubte Größe in Prozent: 100 füllt immer, 30 darf kleiner. */
  kleinste: number
  titel: string
  onFertig: (a: Ausschnitt) => void
  onSchliessen: () => void
}) {
  const texte = useTexte()
  const t = texte.bausteine.ausschnitt
  const [entwurf, setEntwurf] = useState<Ausschnitt>(ausschnitt)
  const [bild, setBild] = useState({ breite: 0, hoehe: 0 })
  const [flaeche, setFlaeche] = useState({ breite: 0, hoehe: 0 })
  const [frei, setFrei] = useState({ breite: 0, hoehe: 0 })
  const rahmen = useRef<HTMLDivElement>(null)
  const platz = useRef<HTMLDivElement>(null)

  /**
   * **Der Griff für die Geste ist ein `useRef`, kein Zustand.**
   *
   * Er wird nirgends gezeichnet und nur im nächsten Ereignis gebraucht; als
   * Zustand hinge er daran, dass zwischen Aufsetzen und Loslassen neu
   * gezeichnet wurde.
   */
  const griff = useRef<{ x: number; y: number; von: Ausschnitt } | null>(null)

  // Escape schließt -- wie jedes andere Blatt. Eines, das nur auf das X hört,
  // ist am Rechner eine Sackgasse.
  useEffect(() => {
    const taste = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSchliessen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [onSchliessen])

  // Die Fläche des Rahmens messen -- `grundStil` rechnet in Bildpunkten, und
  // ein Rahmen mit fester Form ist erst nach dem Zeichnen so breit, wie er ist.
  useEffect(() => {
    const el = rahmen.current
    if (el === null) return
    const messe = () =>
      setFlaeche({ breite: el.clientWidth, hoehe: el.clientHeight })
    messe()
    const wache = new ResizeObserver(messe)
    wache.observe(el)
    return () => wache.disconnect()
  }, [])

  // **Und der Platz, der dem Rahmen bleibt.** Er steht zwischen Kopf und
  // Reglern und ist das, was Kopf, Hinweis, Regler und Knöpfe übrig lassen.
  useEffect(() => {
    const el = platz.current
    if (el === null) return
    const messe = () => setFrei({ breite: el.clientWidth, hoehe: el.clientHeight })
    messe()
    const wache = new ResizeObserver(messe)
    wache.observe(el)
    return () => wache.disconnect()
  }, [])

  /**
   * Der größte Rahmen in der Form der Zielfläche, der in den freien Platz passt.
   *
   * Gerechnet und nicht in CSS: `aspect-ratio` hält seine Form nicht, wenn
   * `max-width` und `max-height` gleichzeitig daran ziehen -- der Browser kürzt
   * eine Seite und lässt die andere stehen.
   */
  const masse = (() => {
    const verhaeltnis = form.breite / form.hoehe
    if (frei.breite < 1 || frei.hoehe < 1) return { breite: 0, hoehe: 0 }
    const breite = Math.min(frei.breite, frei.hoehe * verhaeltnis)
    return { breite: Math.round(breite), hoehe: Math.round(breite / verhaeltnis) }
  })()

  /**
   * Wie weit sich das Bild in dieser Fläche überhaupt bewegen lässt.
   *
   * CSS setzt eine Hintergrundposition von `x%` so, dass sie den Punkt
   * `(Fläche − Bild) · x/100` trifft. Ist das Bild größer als die Fläche, ist
   * die Strecke negativ — dann wandert es beim Ziehen nach rechts, wenn der
   * Wert **sinkt**. Beide Fälle stecken deshalb in derselben Zeile, und der
   * Finger führt das Bild in beiden.
   */
  function verschiebe(dx: number, dy: number, von: Ausschnitt) {
    const fuellend =
      bild.breite > 0 && bild.hoehe > 0 && flaeche.breite > 0
        ? Math.max(flaeche.breite / bild.breite, flaeche.hoehe / bild.hoehe)
        : 0
    if (fuellend === 0) return
    const gezeichnet = {
      breite: bild.breite * fuellend * (von.zoom / 100),
      hoehe: bild.hoehe * fuellend * (von.zoom / 100),
    }
    const strecke = {
      x: flaeche.breite - gezeichnet.breite,
      y: flaeche.hoehe - gezeichnet.hoehe,
    }
    const klemme = (wert: number) => Math.max(0, Math.min(100, Math.round(wert)))
    setEntwurf({
      zoom: von.zoom,
      x: strecke.x === 0 ? von.x : klemme(von.x + (dx / strecke.x) * 100),
      y: strecke.y === 0 ? von.y : klemme(von.y + (dy / strecke.y) * 100),
    })
  }

  const stil = grundStil(entwurf, flaeche, bild)

  return createPortal(
    <div className="bogen bogen--obenauf" role="dialog" aria-modal="true" aria-label={titel}>
      {/* **Ein hohes Blatt, und darin scrollt nichts.** Aus dem Betrieb, iPad:
          „die Einstellungsregler sollten ohne Scrollen sichtbar sein." Ein
          Rahmen mit fester Höhe (46vh) plus Kopf, Hinweis, Regler und drei
          Knöpfen passte in die 55vh des gewöhnlichen Blattes nicht hinein --
          und was nicht hineinpasst, scrollt. Ein Regler, den man erst suchen
          muss, ist keiner.

          Deshalb `--voll` (so hoch wie das Fenster, minus Tastatur) und
          `--fest` (die Höhe steht, statt mit dem Inhalt zu wachsen); der
          Rahmen darunter nimmt sich, was übrig bleibt. */}
      <div className="bogen__blatt bogen__blatt--voll bogen__blatt--fest bogen__blatt--ausschnitt">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{titel}</h3>
          <button
            type="button"
            className="icon-button"
            onClick={onSchliessen}
            aria-label={texte.allgemein.schliessen}
            title={texte.allgemein.schliessen}
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Nur zum Messen der natürlichen Größe. Sichtbar ist das Bild im
            Rahmen darunter, und das ist eine Hintergrundfläche -- ein `<img>`
            könnte dort nicht kleiner sein als sein Kasten. */}
        <img
          src={bildUrl}
          alt=""
          hidden
          onLoad={(event) =>
            setBild({
              breite: event.currentTarget.naturalWidth,
              hoehe: event.currentTarget.naturalHeight,
            })
          }
        />

        <div className="ausschnitt__platz" ref={platz}>
        <div
          ref={rahmen}
          className="ausschnitt__rahmen"
          style={{
            /* **Die Maße kommen gerechnet und nicht aus CSS.**
               `aspect-ratio` hält seine Form nicht, wenn `max-width` und
               `max-height` gleichzeitig daran ziehen: der Browser kürzt eine
               Seite und lässt die andere stehen (gemessen: 488 × 377 statt
               quadratisch). Der freie Platz wird ohnehin gemessen -- daraus
               fällt die Größe von selbst ab, und sie stimmt auf jeder
               Fensterform. */
            width: `${masse.breite}px`,
            height: `${masse.hoehe}px`,
            backgroundImage: `url(${JSON.stringify(bildUrl)})`,
            ...stil,
          }}
          onPointerDown={(event) => {
            griff.current = { x: event.clientX, y: event.clientY, von: entwurf }
            event.currentTarget.setPointerCapture(event.pointerId)
          }}
          onPointerMove={(event) => {
            const g = griff.current
            if (g === null) return
            verschiebe(event.clientX - g.x, event.clientY - g.y, g.von)
          }}
          onPointerUp={() => {
            griff.current = null
          }}
          onPointerCancel={() => {
            griff.current = null
          }}
        />
        </div>

        <p className="feld__hinweis">
          {t.ziehen}
          {kleinste < 100 && t.ganzesBild}
        </p>

        <label className="feld">
          <span className="feld__name">{t.groesse(entwurf.zoom)}</span>
          <input
            type="range"
            min={kleinste}
            max={300}
            step={5}
            value={entwurf.zoom}
            /* Kein `disabled` und kein Speichern zwischendurch: der Entwurf
               liegt hier, gespeichert wird beim Übernehmen. */
            onChange={(event) =>
              setEntwurf((alt) => ({ ...alt, zoom: Number(event.target.value) }))
            }
          />
        </label>

        <div className="ausschnitt__knoepfe">
          <button type="button" className="button" onClick={() => onFertig(entwurf)}>
            {t.uebernehmen}
          </button>
          <button
            type="button"
            className="button button--outline"
            disabled={
              entwurf.x === OHNE.x && entwurf.y === OHNE.y && entwurf.zoom === OHNE.zoom
            }
            onClick={() => setEntwurf(OHNE)}
          >
            {t.zuruecksetzen}
          </button>
          <button type="button" className="button button--outline" onClick={onSchliessen}>
            {texte.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
